import { useState, useEffect, useMemo } from 'react';
import { Product } from '@/types/product';

interface UseBrowseFiltersProps {
  products: Product[];
  isEditMode: boolean;
  typeFilter: string | null;
  searchParams: URLSearchParams;
}

export function useBrowseFilters({
  products,
  isEditMode,
  typeFilter,
  searchParams
}: UseBrowseFiltersProps) {
  const [priceRange, setPriceRange] = useState([0, 15000]);
  const initialCategory = searchParams.get('category');
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategory ? [initialCategory] : []);
  const [circleSelectOnly, setCircleSelectOnly] = useState(false);
  const [selectedBestFor, setSelectedBestFor] = useState<string[]>([]);
  const [selectedIntegrations, setSelectedIntegrations] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('query') || '');
  const [sortBy, setSortBy] = useState('recommended');
  const [showAllIntegrations, setShowAllIntegrations] = useState(false);
  const [selectedSkillLevel, setSelectedSkillLevel] = useState<string>('');
  const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
  const [selectedInclusions, setSelectedInclusions] = useState<string[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  const isEducation = typeFilter === 'education';

  // Sync search term with URL params
  useEffect(() => {
    setSearchTerm(searchParams.get('query') || '');
  }, [searchParams]);

  // Sync category from URL params
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategories([categoryParam]);
    }
  }, [searchParams]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoized filter function
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesPublished = isEditMode || product.is_published !== false;
      const matchesType = typeFilter === 'education' 
        ? product.service_type === 'education'
        : product.service_type !== 'education';
      
      const matchesSearch = !debouncedSearch || 
        product.title?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (product.description && typeof product.description === 'string' && product.description.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
        product.subtitle?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (Array.isArray(product.categories) && product.categories.some((cat: any) => String(cat).toLowerCase().includes(debouncedSearch.toLowerCase()))) ||
        (Array.isArray(product.tags) && product.tags.some((tag: any) => String(tag).toLowerCase().includes(debouncedSearch.toLowerCase())));
      
      const matchesCategory = selectedCategories.length === 0 || 
        (Array.isArray(product.categories) && product.categories.some((cat: any) => selectedCategories.some(slug => String(cat).toLowerCase() === slug.toLowerCase())));
      
      const matchesPrice = product.price >= priceRange[0] && product.price <= priceRange[1];
      const matchesCircleSelect = !circleSelectOnly || product.is_select === true;
      const matchesBestFor = selectedBestFor.length === 0 || (Array.isArray(product.best_for) && selectedBestFor.some(bf => (product.best_for as any[])?.includes(bf)));
      const matchesIntegrations = selectedIntegrations.length === 0 ||
        (Array.isArray((product as any).integration_tags) && selectedIntegrations.every(i => (product as any).integration_tags!.some((tag: string) => tag.toLowerCase() === i.toLowerCase())));
      
      const matchesSkillLevel = !selectedSkillLevel || product.skill_level === selectedSkillLevel;
      const matchesDuration = selectedDurations.length === 0 || 
        selectedDurations.some(duration => {
          const hours = product.total_video_hours || 0;
          if (duration === '<2hr') return hours < 2;
          if (duration === '2-5hr') return hours >= 2 && hours <= 5;
          if (duration === '5-10hr') return hours > 5 && hours <= 10;
          if (duration === '10+hr') return hours > 10;
          return false;
        });
      
      const matchesInclusions = selectedInclusions.length === 0 ||
        selectedInclusions.every(inclusion => {
          if (inclusion === 'certificate') return product.certificate_included === true;
          if (inclusion === 'preview') return !!(product as any).preview_lesson_url;
          if (inclusion === 'lifetime') return product.access_type === 'lifetime';
          return false;
        });
      
      if (isEducation) {
        return matchesPublished && matchesType && matchesSearch && matchesCategory && matchesPrice && 
               matchesSkillLevel && matchesDuration && matchesInclusions;
      }
      
      return matchesPublished && matchesType && matchesSearch && matchesCategory && matchesPrice && matchesCircleSelect && matchesBestFor && matchesIntegrations;
    });
  }, [products, isEditMode, typeFilter, debouncedSearch, selectedCategories, priceRange, circleSelectOnly, selectedBestFor, selectedIntegrations, selectedSkillLevel, selectedDurations, selectedInclusions, isEducation]);

  // Memoized sort function
  const sortedFilteredProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      switch (sortBy) {
        case 'newest': return b.id.localeCompare(a.id);
        case 'most-popular': return (b.student_count || 0) - (a.student_count || 0);
        case 'shortest': return (a.total_video_hours || 0) - (b.total_video_hours || 0);
        case 'longest': return (b.total_video_hours || 0) - (a.total_video_hours || 0);
        case 'price-low': return a.price - b.price;
        case 'price-high': return b.price - a.price;
        case 'rating': return (b.rating || 0) - (a.rating || 0);
        default: return 0;
      }
    });
  }, [filteredProducts, sortBy]);

  const handleCategoryToggle = (categorySlug: string) => {
    setSelectedCategories(prev => prev.includes(categorySlug) ? prev.filter(slug => slug !== categorySlug) : [...prev, categorySlug]);
  };

  const handleBestForToggle = (bestForId: string) => {
    setSelectedBestFor(prev => prev.includes(bestForId) ? prev.filter(id => id !== bestForId) : [...prev, bestForId]);
  };

  const handleIntegrationToggle = (name: string) => {
    setSelectedIntegrations(prev => prev.includes(name) ? prev.filter(i => i !== name) : [...prev, name]);
  };

  const handleDurationToggle = (duration: string) => {
    setSelectedDurations(prev => prev.includes(duration) ? prev.filter(d => d !== duration) : [...prev, duration]);
  };

  const handleInclusionToggle = (inclusion: string) => {
    setSelectedInclusions(prev => prev.includes(inclusion) ? prev.filter(i => i !== inclusion) : [...prev, inclusion]);
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setCircleSelectOnly(false);
    setSelectedBestFor([]);
    setSelectedIntegrations([]);
    setSelectedSkillLevel('');
    setSelectedDurations([]);
    setSelectedInclusions([]);
    setPriceRange([0, 15000]);
  };

  return {
    // State
    priceRange,
    selectedCategories,
    circleSelectOnly,
    selectedBestFor,
    selectedIntegrations,
    searchTerm,
    sortBy,
    showAllIntegrations,
    selectedSkillLevel,
    selectedDurations,
    selectedInclusions,
    isEducation,
    
    // Computed
    sortedFilteredProducts,
    
    // Setters
    setPriceRange,
    setSearchTerm,
    setSortBy,
    setShowAllIntegrations,
    setSelectedSkillLevel,
    setCircleSelectOnly,
    
    // Handlers
    handleCategoryToggle,
    handleBestForToggle,
    handleIntegrationToggle,
    handleDurationToggle,
    handleInclusionToggle,
    clearAllFilters,
  };
}
